'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Loader2, FileCheck2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/lib/auth/context'
import { AVULSO_COMPROVANTE } from '@/lib/billing/plans'
import { trackCheckoutIniciado, trackCheckoutFalhou, trackPremiumCtaClicado } from '@/lib/analytics'
import { cn } from '@/lib/utils'

interface AvulsoComprovanteButtonProps {
  className?: string
  label?: string
}

/**
 * Inicia checkout Mercado Pago para 1 comprovante limpo (avulso).
 * Deslogado → sign-up com next de volta ao comprovante.
 */
export function AvulsoComprovanteButton({
  className,
  label = `Comprar 1 limpo — ${AVULSO_COMPROVANTE.priceDisplay}`,
}: AvulsoComprovanteButtonProps) {
  const { user } = useAuth()
  const router = useRouter()
  const [loading, setLoading] = useState(false)

  async function handleClick() {
    trackPremiumCtaClicado('comprovante_avulso')

    if (!user) {
      router.push('/sign-up?next=/comprovante-de-faturamento-mei')
      return
    }

    setLoading(true)
    try {
      const res = await fetch('/api/billing/checkout-avulso', { method: 'POST' })
      const data = await res.json().catch(() => null)

      if (res.status === 401) {
        router.push('/sign-up?next=/comprovante-de-faturamento-mei')
        return
      }

      if (data?.url) {
        trackCheckoutIniciado('comprovante_avulso')
        window.location.href = data.url
        return
      }

      trackCheckoutFalhou(data?.error || 'avulso')
      toast.error(data?.error || 'Não deu pra iniciar o pagamento. Tente de novo.')
    } catch {
      trackCheckoutFalhou('conexao')
      toast.error('Erro de conexão. Tente de novo.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-[13.5px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60',
        className,
      )}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <FileCheck2 className="h-4 w-4" />
      )}
      {label}
    </button>
  )
}
